import React, { useEffect, useMemo, useRef } from 'react';

import cs from 'classnames';

import useLoading from 'lib/hooks/useLoading';
import { useFileStack } from 'lib/hooks/useFileStack';

import Button from '../Button/Button';
import { showAlert } from '../Alert/Alert';
import Typography from '../Typography/Typography';
import { getAcceptAttribute, isFileAllowed } from '../FileUploader/lib/helpers';

import Image from './components/Image';
import { IMAGE_UPLOADER_SUBTITLE, IMAGE_UPLOADER_TITLE } from './lib/constants';

const FORMATS = '.jpg, .jpeg, .png';

/**
 * @param {import('./lib/propTypes').ImageUploaderProps} props
 */

const ImageUploader = ({
  handlers = [],
  onUpload = (_file) => {},
  onDelete = (_file) => {},
  fileStackPicker = false,
  className = '',
  type = 'avatar',
  imageClassName = ''
}) => {
  const fileInputRef = useRef(null);
  const {
    files,
    addFile,
    readFile,
    deleteFile,
    openFilePicker,
    loading: fileStackLoading
  } = useFileStack();

  const { loading, startLoading, stopLoading } = useLoading();

  const file = files[files.length - 1];
  const isLoading = loading || fileStackLoading;

  const isAvatar = type === 'avatar';

  const handleOpenPicker = () => {
    if (fileStackPicker) {
      openFilePicker(onUpload);
      return;
    }

    fileInputRef.current.click();
  };
  const handleDelete = async () => deleteFile(files, onDelete);

  const handleDownload = async () => {
    if (handlers && handlers.length > 0) {
      startLoading();
      await Promise.all(handlers.map(async (handle) => !!handle && readFile(handle))).finally(
        () => {
          stopLoading();
        }
      );
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (isFileAllowed(file, FORMATS)) {
        startLoading();
        const newFile = await addFile(file, onUpload);
        await readFile(newFile?.handle);
        stopLoading();
      } else {
        showAlert({
          color: 'danger',
          message: 'Invalid file format. Please upload files in the following formats: ' + FORMATS
        });
      }
    }
  };

  useEffect(() => {
    handleDownload();
  }, [handlers]);

  const allowedFormats = useMemo(() => getAcceptAttribute(FORMATS), []);

  return (
    <div className={cs('flex flex-1 gap-[10px]', className)}>
      <Image
        url={file?.url}
        icon={isAvatar ? 'avatar' : 'no-image-round'}
        loading={isLoading}
        className={imageClassName}
      />
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <Typography color="neutral-800" weight="medium">
            {IMAGE_UPLOADER_TITLE}
          </Typography>
          <Typography size="small" color="neutral-700">
            {IMAGE_UPLOADER_SUBTITLE}
          </Typography>
        </div>
        <div className="flex items-center gap-[7px]">
          {file ? (
            <>
              <Button
                text="Remove"
                size="small"
                color="danger"
                style="outlined"
                isDisabled={isLoading}
                onClick={handleDelete}
                type="button"
              />
              <Button
                text="Change"
                size="small"
                variant="tertiary"
                isDisabled={isLoading}
                onClick={handleOpenPicker}
                type="button"
              />
            </>
          ) : (
            <Button
              size="small"
              text="Upload"
              variant="tertiary"
              isDisabled={isLoading}
              onClick={handleOpenPicker}
              type="button"
            />
          )}
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleUpload}
        accept={allowedFormats}
        onClick={(e) => {
          e.target.value = null;
        }}
      />
    </div>
  );
};

export default ImageUploader;
